// WHAT: Morning affirmations selection step component for Miracle Flow sessions
// WHY:  Provides UI for users to select and interact with morning affirmations during session workflow
// DOMAIN: Productivity → Miracle Flow → Session Steps
// DEPENDENCIES: affirmationApi service, Affirmation types, React state management
// RISKS:
//   - NOTE: API call on mount may fail silently - error handling only logs to console
//   - NOTE: Selected affirmations state is managed in parent - may cause sync issues
//   - NOTE: Grid layout assumes responsive breakpoints - may not work on all screen sizes

import { Heart, CheckCircle } from "lucide-react";
import React, { useState, useEffect } from "react";

import { Card, Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { type Affirmation } from "../../../types/affirmation";
import { affirmationApi } from "../../../utils/api-client";

export interface AffirmationsStepProperties {
  className?: string;
  onAffirmationToggle: (id: string) => void;
  selectedAffirmations: Set<string>;
  step: "Affirmations";
}

/**
 * WHAT:
 *   Morning affirmations selection step component for Miracle Flow sessions.
 *
 * WHY:
 *   Provides interactive UI for users to select morning affirmations during session workflow,
 *   enabling personalized daily motivation and goal-setting.
 *
 * PROPS:
 *   - step: 'Affirmations' — Step identifier (currently fixed)
 *   - selectedAffirmations: Set<string> — Set of selected affirmation IDs managed by parent
 *   - onAffirmationToggle: (id: string) => void — Callback to toggle affirmation selection
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Loads morning affirmations from API on mount via useEffect
 *   - Displays affirmations in responsive grid layout (1/2/3 columns based on screen size)
 *   - Tracks selection state via parent-controlled Set
 *   - Shows loading spinner during API fetch
 *   - Displays empty state message if no affirmations available
 *
 * RISKS:
 *   - NOTE: API call on mount may fail silently - error handling only logs to console
 *   - NOTE: Selected affirmations state is managed in parent - may cause sync issues if parent unmounts
 *   - NOTE: Grid layout assumes responsive breakpoints - may not work correctly on all screen sizes
 *   - WARN: No error UI feedback - users may not know if affirmations failed to load
 *
 * @param root0
 *
 * @param root0.step
 *
 * @param root0.selectedAffirmations
 *
 * @param root0.onAffirmationToggle
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const AffirmationsStep: React.FC<AffirmationsStepProperties> = ({
  className,
  onAffirmationToggle,
  selectedAffirmations,
  step,
}) => {
  const [affirmations, setAffirmations] = useState<Affirmation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // NOTE: Load affirmations on component mount - no dependency array means it runs once
  useEffect(() => {
    void loadAffirmations();
  }, []);

  /**
   * WHAT:
   *   Fetches morning affirmations from the API and updates component state.
   *
   * WHY:
   *   Separates data fetching logic from component render for better error handling and loading states.
   *
   * SIDE EFFECTS:
   *   - Sets isLoading to true before fetch, false after
   *   - Updates affirmations state with fetched data
   *   - Logs errors to console (no user-visible error handling)
   *
   * RISKS:
   *   - NOTE: Error handling only logs to console - users won't see failures
   *   - NOTE: No retry mechanism - failed requests require manual refresh
   */
  const loadAffirmations = async () => {
    setIsLoading(true);
    try {
      const data = await affirmationApi.getMorningAffirmations();
      setAffirmations(data);
    } catch {
      // NOTE: Failed to load affirmations - consider showing user-facing error UI
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("space-y-6 w-full", className)} data-step={step}>
      {/* Hero Section */}
      <Stack align="center" className="py-4 w-full" direction="col" gap={4}>
        <div className="flex items-center justify-center w-20 h-20 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
          <Heart className="w-10 h-10 text-white" />
        </div>
        <Stack align="center" className="w-full max-w-4xl" direction="col" gap={2}>
          <Heading className="font-bold text-center text-tracq-bright-blue" level="h3" size="xl">
            Affirmations
          </Heading>
          <p className="text-[#CBD5E1] text-center text-lg w-full px-4">
            Select affirmations that resonate with you today. Read them aloud and feel their power.
          </p>
        </Stack>
      </Stack>

      {/* Affirmations Grid */}
      {isLoading ? (
        <Stack align="center" className="py-12 w-full" direction="col" gap={4}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tracq-bright-blue" />
          <p className="text-[#94A3B8]">Loading affirmations...</p>
        </Stack>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
          {affirmations.map((affirmation) => {
            const isSelected = selectedAffirmations.has(affirmation.id);
            return (
              <Card
                className={cn(
                  "cursor-pointer transition-all duration-200 hover:shadow-tracq-card",
                  isSelected
                    ? "bg-tracq-bright-blue/20 border-tracq-bright-blue/50"
                    : "bg-[#334155] border-[#475569] hover:bg-[#475569]",
                )}
                key={affirmation.id}
                onClick={() => {
                  onAffirmationToggle(affirmation.id);
                }}
                padding="md"
                variant="default"
              >
                <Stack align="center" direction="row" gap={3}>
                  <div
                    className={cn(
                      "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200",
                      isSelected
                        ? "border-tracq-bright-blue bg-tracq-bright-blue"
                        : "border-[#475569]",
                    )}
                  >
                    {isSelected ? <CheckCircle className="w-4 h-4 text-white" /> : null}
                  </div>
                  <p className="text-[#CBD5E1] text-sm leading-relaxed flex-1">
                    {affirmation.text}
                  </p>
                </Stack>
              </Card>
            );
          })}
        </div>
      )}

      {affirmations.length === 0 && !isLoading && (
        <Stack align="center" className="py-8" direction="col" gap={2}>
          <p className="text-[#94A3B8] text-center">
            No morning affirmations available. Add some in the Affirmation Settings.
          </p>
        </Stack>
      )}
    </div>
  );
};
